"use client";

import { useCallback, useMemo } from "react";
import {
  ComposeFormStateStableContextValue,
  ComposeWatchedFieldsContextValue,
  ComposeRecipientsContextValue,
  ComposeClientContextValue,
  ComposeEmailFormatContextValue,
} from "@/lib/core/crm/types";
import { ComposeTab, VariableValues } from "@/lib/types/compose-modal-types";

/**
 * Compose context VALUES are built here as hooks, not plain factory functions.
 *
 * A context value that is a fresh object literal on every parent render makes
 * every consumer of that context re-render on every keystroke, regardless of
 * whether the slice it reads changed. Each builder below therefore `useMemo`s
 * its result over the exact fields it copies, and every callback it embeds is
 * `useCallback`'d (a fresh closure inside a memo defeats the memo).
 */

/** The `useCompose(...)` fields the STABLE form-state context value is built from. */
export interface ComposeFormStateSource
  extends Pick<
    ComposeFormStateStableContextValue,
    | "form"
    | "activeTab"
    | "hasUnfilledVariables"
    | "variableValues"
    | "showVariablePreview"
  > {
  setActiveTab: (tab: ComposeTab) => void;
  setShowVariablePreview: (show: boolean) => void;
  handleVariableUpdate: (variables: VariableValues) => void;
}

/** The `useCompose(...)` fields the volatile watched-fields context is built from. */
export type ComposeWatchedFieldsSource = ComposeWatchedFieldsContextValue;

/** The `useCompose(...)` fields the recipients context value is built from. */
export interface ComposeRecipientsSource
  extends Pick<
    ComposeRecipientsContextValue,
    "recipients" | "ccRecipients" | "bccRecipients" | "ccBccMode"
  > {
  setRecipients: ComposeRecipientsContextValue["onRecipientsChange"];
  setCcBccMode: ComposeRecipientsContextValue["setCcBccMode"];
  setCcRecipients: ComposeRecipientsContextValue["setCcRecipients"];
  setBccRecipients: ComposeRecipientsContextValue["setBccRecipients"];
}

/** The `useCompose(...)` fields the client context value is built from. */
export interface ComposeClientSource
  extends Pick<
    ComposeClientContextValue,
    | "selectedClient"
    | "clientOptions"
    | "filteredHubUsers"
    | "hubUsersLoading"
    | "clientsLoading"
    | "isSearching"
    | "clientSearchTerm"
    | "existingContact"
    | "personalizationOrigin"
  > {
  handleClientSelect: ComposeClientContextValue["onClientSelect"];
  setClientSearchTerm: ComposeClientContextValue["onClientSearchChange"];
}

/**
 * Builds the STABLE form-state context value. Deliberately does not read
 * `watchedSubject`/`watchedBody` — those go through
 * `useWatchedFieldsContextValue` so typing cannot invalidate this value.
 */
export function useFormStateContextValue(
  composeForm: ComposeFormStateSource
): ComposeFormStateStableContextValue {
  const {
    form,
    activeTab,
    hasUnfilledVariables,
    variableValues,
    showVariablePreview,
    setActiveTab,
    setShowVariablePreview,
    handleVariableUpdate,
  } = composeForm;

  const onTabChange = useCallback((tab: ComposeTab) => setActiveTab(tab), [setActiveTab]);

  const onToggleVariablePreview = useCallback(
    () => setShowVariablePreview(!showVariablePreview),
    [setShowVariablePreview, showVariablePreview]
  );

  return useMemo(
    () => ({
      form,
      activeTab,
      hasUnfilledVariables,
      variableValues,
      showVariablePreview,
      onTabChange,
      onVariableUpdate: handleVariableUpdate,
      onToggleVariablePreview,
    }),
    [
      form,
      activeTab,
      hasUnfilledVariables,
      variableValues,
      showVariablePreview,
      onTabChange,
      handleVariableUpdate,
      onToggleVariablePreview,
    ]
  );
}

/**
 * Builds the volatile watched-fields context value. This one legitimately
 * changes on every keystroke; memoizing it still matters so that a render
 * caused by something OTHER than typing does not churn its consumers.
 */
export function useWatchedFieldsContextValue(
  composeForm: ComposeWatchedFieldsSource
): ComposeWatchedFieldsContextValue {
  const { watchedSubject, watchedBody } = composeForm;

  return useMemo(
    () => ({ watchedSubject, watchedBody }),
    [watchedSubject, watchedBody]
  );
}

export function useRecipientsContextValue(
  composeForm: ComposeRecipientsSource
): ComposeRecipientsContextValue {
  const {
    recipients,
    ccRecipients,
    bccRecipients,
    ccBccMode,
    setRecipients,
    setCcBccMode,
    setCcRecipients,
    setBccRecipients,
  } = composeForm;

  return useMemo(
    () => ({
      recipients,
      ccRecipients,
      bccRecipients,
      ccBccMode,
      onRecipientsChange: setRecipients,
      setCcBccMode,
      setCcRecipients,
      setBccRecipients,
    }),
    [
      recipients,
      ccRecipients,
      bccRecipients,
      ccBccMode,
      setRecipients,
      setCcBccMode,
      setCcRecipients,
      setBccRecipients,
    ]
  );
}

export function useClientContextValue(
  composeForm: ComposeClientSource
): ComposeClientContextValue {
  const {
    selectedClient,
    clientOptions,
    filteredHubUsers,
    hubUsersLoading,
    clientsLoading,
    isSearching,
    clientSearchTerm,
    existingContact,
    personalizationOrigin,
    handleClientSelect,
    setClientSearchTerm,
  } = composeForm;

  return useMemo(
    () => ({
      selectedClient,
      clientOptions,
      filteredHubUsers,
      hubUsersLoading,
      clientsLoading,
      isSearching,
      clientSearchTerm,
      existingContact,
      personalizationOrigin,
      onClientSelect: handleClientSelect,
      onClientSearchChange: setClientSearchTerm,
    }),
    [
      selectedClient,
      clientOptions,
      filteredHubUsers,
      hubUsersLoading,
      clientsLoading,
      isSearching,
      clientSearchTerm,
      existingContact,
      personalizationOrigin,
      handleClientSelect,
      setClientSearchTerm,
    ]
  );
}

/**
 * The email-format context is entirely static on this surface (HTML compose is
 * not wired here), so it is allocated ONCE at module scope — the strongest
 * stability available. The hook wrapper keeps the call site symmetrical with
 * the three above.
 */
const STATIC_EMAIL_FORMAT_CONTEXT_VALUE: ComposeEmailFormatContextValue = {
  emailFormat: "plain",
  htmlEmailData: { to: "", subject: "", body: "" },
  htmlValidationErrors: [],
  onEmailFormatChange: () => {},
  onHtmlEmailChange: () => {},
};

export function useEmailFormatContextValue(): ComposeEmailFormatContextValue {
  return STATIC_EMAIL_FORMAT_CONTEXT_VALUE;
}
